/**
 * Google Cloud Vision API Service
 * This is an optional enhancement for better OCR accuracy
 * 
 * To enable Google Vision API:
 * 1. Create a Google Cloud Project
 * 2. Enable the Vision API
 * 3. Create credentials and get an API key
 * 4. Add the API key to your .env.local file as REACT_APP_GOOGLE_VISION_API_KEY
 * 5. Set USE_GOOGLE_VISION to true in ImageAnalysisService
 */

class GoogleVisionService {
  constructor() {
    this.apiKey = process.env.REACT_APP_GOOGLE_VISION_API_KEY;
    this.apiUrl = 'https://vision.googleapis.com/v1/images:annotate';
    this.isEnabled = !!this.apiKey;
  }

  /**
   * Check if Google Vision API is configured and available
   */
  isAvailable() {
    return this.isEnabled;
  }

  /**
   * Perform OCR using Google Vision API
   */
  async performOCR(imageData) {
    if (!this.isAvailable()) {
      throw new Error('Google Vision API is not configured');
    }

    try {
      // Convert image to base64 if it's a data URL
      const base64Image = imageData.includes('base64,') 
        ? imageData.split('base64,')[1] 
        : imageData;

      const requestBody = {
        requests: [
          {
            image: {
              content: base64Image
            },
            features: [
              {
                type: 'TEXT_DETECTION',
                maxResults: 1
              },
              {
                type: 'LABEL_DETECTION',
                maxResults: 10
              },
              {
                type: 'LOGO_DETECTION',
                maxResults: 5
              }
            ]
          }
        ]
      };

      const response = await fetch(`${this.apiUrl}?key=${this.apiKey}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        throw new Error(`Google Vision API error: ${response.statusText}`);
      }

      const data = await response.json();
      
      if (data.responses && data.responses[0]) {
        const result = data.responses[0];
        
        return {
          text: result.fullTextAnnotation?.text || '',
          textAnnotations: result.textAnnotations || [],
          labels: result.labelAnnotations || [],
          logos: result.logoAnnotations || [],
          confidence: this.calculateConfidence(result),
          rawResponse: result
        };
      }

      return {
        text: '',
        textAnnotations: [],
        labels: [],
        logos: [],
        confidence: 0,
        error: 'No response from Google Vision API'
      };
    } catch (error) {
      throw error;
    }
  }

  /**
   * Analyze labels to detect product type
   */
  analyzeLabels(labels) {
    const productKeywords = {
      beverage: ['drink', 'beverage', 'soda', 'cola', 'juice', 'water', 'coffee', 'tea'],
      snack: ['snack', 'chips', 'crackers', 'popcorn', 'nuts'],
      candy: ['candy', 'chocolate', 'sweet', 'confectionery', 'dessert'],
      food: ['food', 'meal', 'cuisine', 'dish', 'ingredient']
    };

    const detectedTypes = [];
    
    labels.forEach(label => {
      const description = label.description.toLowerCase();
      
      for (const [type, keywords] of Object.entries(productKeywords)) {
        if (keywords.some(keyword => description.includes(keyword))) {
          detectedTypes.push({
            type,
            confidence: label.score * 100,
            label: label.description
          });
        }
      }
    });

    return detectedTypes;
  }

  /**
   * Extract brand information from logos
   */
  extractBrands(logos) {
    return logos.map(logo => ({
      name: logo.description,
      confidence: logo.score * 100,
      boundingBox: logo.boundingPoly
    }));
  }

  /**
   * Calculate overall confidence score
   */
  calculateConfidence(result) {
    let confidence = 0;
    let factors = 0;

    // Text detection confidence
    if (result.fullTextAnnotation) {
      confidence += 80;
      factors++;
    }

    // Label detection confidence
    if (result.labelAnnotations && result.labelAnnotations.length > 0) {
      const avgLabelScore = result.labelAnnotations.reduce((sum, label) => sum + label.score, 0) / result.labelAnnotations.length;
      confidence += avgLabelScore * 100;
      factors++;
    }

    // Logo detection confidence
    if (result.logoAnnotations && result.logoAnnotations.length > 0) {
      confidence += 90;
      factors++;
    }

    return factors > 0 ? Math.round(confidence / factors) : 0;
  }

  /**
   * Process ingredients text with better formatting
   */
  processIngredientsText(text) {
    if (!text) return '';

    // Look for ingredients section
    const ingredientsMatch = text.match(/ingredients[:\s]*([\s\S]*?)(?:nutrition|allergen|manufactured|$)/i);
    
    if (ingredientsMatch && ingredientsMatch[1]) {
      let ingredients = ingredientsMatch[1];
      
      // Clean up formatting
      ingredients = ingredients
        .replace(/\n+/g, ' ')
        .replace(/\s+/g, ' ')
        .replace(/[()]/g, match => ` ${match} `)
        .trim();
      
      return `INGREDIENTS: ${ingredients}`;
    }

    return text;
  }

  /**
   * Main analysis function using Google Vision
   */
  async analyzeWithGoogleVision(imageData) {
    try {
      const visionResult = await this.performOCR(imageData);
      
      const productTypes = this.analyzeLabels(visionResult.labels);
      const brands = this.extractBrands(visionResult.logos);
      const processedText = this.processIngredientsText(visionResult.text);
      
      return {
        success: true,
        provider: 'Google Vision API',
        text: processedText,
        rawText: visionResult.text,
        confidence: visionResult.confidence,
        productTypes,
        brands,
        labels: visionResult.labels.map(l => ({
          name: l.description,
          confidence: Math.round(l.score * 100)
        })),
        hasText: visionResult.text.length > 10,
        isProbablyFood: this.checkIfFoodProduct(visionResult)
      };
    } catch (error) {
      return {
        success: false,
        provider: 'Google Vision API',
        error: error.message,
        text: '',
        confidence: 0
      };
    }
  }

  /**
   * Check if detected labels indicate a food product
   */
  checkIfFoodProduct(visionResult) {
    const foodLabels = ['food', 'snack', 'beverage', 'drink', 'ingredient', 'nutrition', 'packaged goods'];
    
    if (visionResult.labels) {
      return visionResult.labels.some(label => 
        foodLabels.some(foodLabel => 
          label.description.toLowerCase().includes(foodLabel)
        )
      );
    }
    
    return false;
  }
}

export default new GoogleVisionService();